export interface ServiceLifecycle {
    initialize(): void | Promise<void>
    resume(): void | Promise<void>
    suspend(): void | Promise<void>
    destroy(): void | Promise<void>
}

type LifecycleHook = 'destroy' | 'resume' | 'suspend'

export class OrderedServiceLifecycle implements ServiceLifecycle {
    readonly #services: readonly ServiceLifecycle[]
    #destroyed = false
    #initialized = false
    #initializedServices: ServiceLifecycle[] = []

    public constructor(services: readonly ServiceLifecycle[]) {
        this.#services = services
    }

    public async initialize(): Promise<void> {
        if (this.#destroyed) {
            throw new Error('Cannot initialize a destroyed service lifecycle.')
        }

        if (this.#initialized) {
            return
        }

        try {
            for (const service of this.#services) {
                await service.initialize()
                this.#initializedServices.push(service)
            }

            this.#initialized = true
        } catch (error) {
            const rollbackErrors = await this.#runHook(
                [...this.#initializedServices].reverse(),
                'destroy'
            )
            this.#initializedServices = []
            this.#destroyed = true

            if (rollbackErrors.length) {
                throw new AggregateError(
                    [error, ...rollbackErrors],
                    'Service initialization failed and rollback reported errors.'
                )
            }

            throw error
        }
    }

    public async resume(): Promise<void> {
        this.#assertAvailable('resume')
        const errors = await this.#runHook(this.#initializedServices, 'resume')
        this.#throwHookErrors('resume', errors)
    }

    public async suspend(): Promise<void> {
        this.#assertAvailable('suspend')
        const errors = await this.#runHook([...this.#initializedServices].reverse(), 'suspend')
        this.#throwHookErrors('suspend', errors)
    }

    public async destroy(): Promise<void> {
        if (this.#destroyed) {
            return
        }

        this.#destroyed = true
        const errors = await this.#runHook([...this.#initializedServices].reverse(), 'destroy')
        this.#initializedServices = []
        this.#initialized = false
        this.#throwHookErrors('destroy', errors)
    }

    async #runHook(services: readonly ServiceLifecycle[], hook: LifecycleHook): Promise<unknown[]> {
        const errors: unknown[] = []

        for (const service of services) {
            try {
                await service[hook]()
            } catch (error) {
                errors.push(error)
            }
        }

        return errors
    }

    #assertAvailable(hook: LifecycleHook): void {
        if (this.#destroyed) {
            throw new Error(`Cannot ${hook} a destroyed service lifecycle.`)
        }

        if (!this.#initialized) {
            throw new Error(`Cannot ${hook} an uninitialized service lifecycle.`)
        }
    }

    #throwHookErrors(hook: LifecycleHook, errors: unknown[]): void {
        if (errors.length) {
            throw new AggregateError(errors, `Service ${hook} reported errors.`)
        }
    }
}
